import { BookingSteps, IsDiagnosed, IsLegalGuardian, IsParent, IsZipCodeSupported } from '@app/store/booking/types'
import { segmentEventsNames } from '@app/utils/analytics'
import { AbTestVariants } from '@app/store/ui'
import { screensData } from '@ui/pages/Booking/screensData'
import { BookingFlowDefinition } from '../types'
import { ROUTES } from '@app/constants'
import { breakPoints } from '@ui/theme'
import { runtimeConfig } from '@app/common/config'
import { AbTestSplit, selfServeFlowVariants, UnifiedBookingFlowLaunchDarklyFlags } from './types'
import { defineTestVariant } from './utils'

export function selfServeFlow(ldFlags: UnifiedBookingFlowLaunchDarklyFlags): BookingFlowDefinition {
  return {
    flowId: 'self-serve-flow',
    testVariantId: AbTestVariants.Control,
    stepsNumber: 8, // should be max(stepNumber) and not steps.length
    initFlow({ context }) {
      if (context.zipCode) {
        if (context.bookingState.inputData.isZipCodeSupported === IsZipCodeSupported.No) {
          return BookingSteps.PostalCodeOfframp
        } else {
          return BookingSteps.ServiceSelection
        }
      }
      return BookingSteps.ZipCode
    },
    includeDiagnosisAsNonQualifyingReason: false,
    steps: [
      // STEPS

      // Step: ZipCode
      {
        stepId: BookingSteps.ZipCode,
        stepNumber: 1,

        heading: screensData.zipCode.heading,
        description: screensData.zipCode.description,
        initBookingflow: true,
        segmentEventNames: {
          bookingStarted: segmentEventsNames.BOOKING_FLOW_STARTED,
          postalCodeCompleted: segmentEventsNames.BOOKING_FLOW_POSTAL_CODE_COMPLETED
        },

        actions: [
          // don't ask for zip code again
          {
            actionId: 'initStep',
            command: ({ backStep, context: { zipCode, bookingState } }) => {
              if (!backStep && zipCode) {
                if (bookingState.inputData.isZipCodeSupported === IsZipCodeSupported.Yes) {
                  return { action: { actionId: 'nextStep' } }
                } else {
                  return { stepTo: BookingSteps.PostalCodeOfframp }
                }
              }
              return undefined
            }
          },
          {
            actionId: 'nextStep',
            command({ context: { zipCode, bookingState } }) {
              if (zipCode && bookingState.inputData.isZipCodeSupported === IsZipCodeSupported.Yes) {
                return { stepTo: BookingSteps.ServiceSelection }
              } else {
                return { stepTo: BookingSteps.PostalCodeOfframp }
              }
            }
          }
        ]
      },

      // Step: Service Selection
      {
        stepId: BookingSteps.ServiceSelection,
        stepNumber: 2,
        contentMaxWidth: 116.5,

        heading: screensData.serviceSeletion.heading,
        segmentEventName: segmentEventsNames.BOOKING_FLOW_SERVICE_SELECTION_COMPLETED,

        actions: [
          { actionId: 'nextStep', command: { stepTo: BookingSteps.AgeInYears } },
          { actionId: 'previousStep', command: { stepBackTo: BookingSteps.ZipCode } }
        ]
      },

      // Step: Age
      {
        stepId: BookingSteps.AgeInYears,
        stepNumber: 3,

        heading: screensData.ageInYears.heading,
        description: screensData.ageInYears.description,
        segmentEventName: segmentEventsNames.BOOKING_FLOW_AGE_COMPLETED,

        actions: [
          {
            actionId: 'nextStep',
            command({ age }) {
              return { stepTo: age && age >= 10 ? BookingSteps.AgeOfframp : BookingSteps.IsParent }
            }
          },
          { actionId: 'previousStep', command: { stepBackTo: BookingSteps.ServiceSelection } }
        ]
      },

      // Step: IsParent
      {
        stepId: BookingSteps.IsParent,
        stepNumber: 4,

        heading: screensData.isParent.heading,
        description: screensData.isParent.description,
        segmentEventName: segmentEventsNames.BOOKING_FLOW_IS_PARENT_COMPLETED,

        actions: [
          {
            actionId: 'nextStep',
            command: ({ isParent }) => {
              const stepTo = isParent === IsParent.No ? BookingSteps.LegalGuardian : BookingSteps.Diagnosis
              return { stepTo }
            }
          },
          { actionId: 'previousStep', command: { stepBackTo: BookingSteps.AgeInYears } }
        ]
      },

      // Step: LegalGuardian
      {
        stepId: BookingSteps.LegalGuardian,
        stepNumber: 5,

        heading: screensData.legalGuardian.heading,
        description: screensData.legalGuardian.description,
        segmentEventName: segmentEventsNames.BOOKING_FLOW_LEGAL_GUARDIAN_COMPLETED,

        actions: [
          {
            actionId: 'nextStep',
            command: ({ isLegalGuardian }) => {
              const stepTo =
                isLegalGuardian === IsLegalGuardian.No ? BookingSteps.LegalGuardianOfframp : BookingSteps.Diagnosis
              return { stepTo }
            }
          },
          { actionId: 'previousStep', command: { stepBackTo: BookingSteps.IsParent } }
        ]
      },

      // Step: Diagnosis
      {
        stepId: BookingSteps.Diagnosis,
        stepNumber: 6,

        heading: screensData.diagnosis.heading,
        description: screensData.diagnosis.description,
        segmentEventName: segmentEventsNames.BOOKING_FLOW_DIAGNOSIS_COMPLETED,

        actions: [
          {
            actionId: 'nextStep',
            command: ({ isZipCodeSupported, isDiagnosed }) => {
              if (isDiagnosed === IsDiagnosed.Yes) {
                let testVariant: AbTestSplit
                switch (ldFlags.selfServeFlow) {
                  case selfServeFlowVariants.On:
                    testVariant = AbTestSplit.Test
                    break
                  case selfServeFlowVariants.Off:
                    testVariant = AbTestSplit.Control
                    break
                  case selfServeFlowVariants.HalfSplit:
                    testVariant = defineTestVariant()
                    break
                  default:
                    testVariant = AbTestSplit.Control
                    break
                }

                const nextStep = testVariant === AbTestSplit.Test ? BookingSteps.CreateAccount : BookingSteps.ParentInfo
                return { stepTo: nextStep }
              }

              if (isZipCodeSupported === IsZipCodeSupported.Yes) {
                return { stepTo: BookingSteps.DiagnosisOfframp }
              }

              return { stepTo: BookingSteps.DiagnosisOfframpWithoutSupport }
            }
          },
          { actionId: 'previousStep', command: { stepBackUsingHistory: true } }
        ]
      },

      // Step: Create Account - A/B Variant *Test*
      {
        stepId: BookingSteps.CreateAccount,
        stepNumber: 8,

        heading: screensData.createAccount.heading,
        description: screensData.createAccount.description,

        actions: [
          {
            actionId: 'nextStep',
            command({ context }) {
              window.location.href = runtimeConfig.PARENT_BASE_URL as string
              return {}
            }
          },
          { actionId: 'previousStep', command: { stepBackUsingHistory: true } }
        ]
      },

      // Step: ParentInfo - A/B Variant *Control*
      {
        stepId: BookingSteps.ParentInfo,
        stepNumber: 8,

        heading: screensData.parentInfo.heading,
        description: screensData.parentInfo.description,
        showEmail: true,

        actions: [
          { actionId: 'nextStep', command: { stepTo: BookingSteps.TimeSlots } },
          {
            actionId: 'previousStep',
            command: ({
              context: {
                bookingState: {
                  inputData: { isInsured }
                }
              }
            }) => ({
              stepBackTo: BookingSteps.Diagnosis
            })
          }
        ]
      },

      // SCHEDULING

      // TimeSlots
      {
        stepId: BookingSteps.TimeSlots,
        heading: screensData.timeSlots.heading,

        description: ({ isWorkingTime }) =>
          isWorkingTime
            ? screensData.timeSlots.description.workingHours
            : screensData.timeSlots.description.nonWorkingHours,

        segmentEventNames: {
          callNow: segmentEventsNames.BOOKING_FLOW_CALL_REQUESTED
        },
        callNowTitle: 'Call Me Now',
        callLaterTitle: 'Schedule a Call',
        continueTitle: 'Continue',
        popUpTitle: 'Choose a Date and Time for a Call',
        popUpSubTitle: 'Please choose a date and time for a 30 minute call below.',
        facebookPixelEventsBookingCallMeNowClickedOnlyWithSessionId: false,
        googleAnalyticsEventsCallRequested: true,

        actions: [
          { actionId: 'callLater', command: { stepTo: BookingSteps.SchedulingConfirmation } },
          { actionId: 'callNow', command: { stepTo: BookingSteps.CallNowConfirmation } }
        ]
      },

      // SchedulingConfirmation
      {
        stepId: BookingSteps.SchedulingConfirmation,
        heading: screensData.schedulingConfirmation.heading,

        description: ({ windowWidth }) =>
          windowWidth <= breakPoints.laptopL
            ? screensData.schedulingConfirmation.description.mobile
            : screensData.schedulingConfirmation.description.desktop,

        segmentEventName: segmentEventsNames.BOOKING_FLOW_CALL_SCHEDULED,
        confirmationButtonTall: false,
        showPhoneNumber: false,
        confirmButtonTitle: 'Confirm',
        googleAnalyticsEventsCallScheduled: true,

        actions: [
          { actionId: 'nextStep', command: { stepTo: BookingSteps.CallLaterConfirmation } },
          { actionId: 'previousStep', command: { stepBackTo: BookingSteps.TimeSlots } }
        ]
      },

      // FINAL SCREENS

      // CallNowConfirmation
      {
        stepId: BookingSteps.CallNowConfirmation,

        heading: screensData.callConfirmation.callNow.heading,
        description: screensData.callConfirmation.callNow.description,
        imageSrc: screensData.callConfirmation.callNow.imageSrc,
        imageAlt: screensData.callConfirmation.callNow.imageAlt,

        segmentEventName: segmentEventsNames.BOOKING_FLOW_ADDED_TO_CALENDAR,
        documentsForCallDataVersion: 'control',

        actions: []
      },

      // CallLaterConfirmation
      {
        stepId: BookingSteps.CallLaterConfirmation,
        heading: screensData.callConfirmation.callLater.heading,
        description: screensData.callConfirmation.callLater.description,

        segmentEventName: segmentEventsNames.BOOKING_FLOW_ADDED_TO_CALENDAR,
        showPhoneNumber: false,
        documentsForCallDataVersion: 'control',
        goToHomePageLinkTitle: 'Go to Homepage',

        actions: []
      },

      // DiagnosisOfframp
      {
        stepId: BookingSteps.DiagnosisOfframp,

        heading: screensData.diagnosisOfframp.heading,
        description: screensData.diagnosisOfframp.description,
        imageSrc: screensData.diagnosisOfframp.imageSrc,
        imageAlt: screensData.diagnosisOfframp.imageAlt,

        segmentEventName: segmentEventsNames.BOOKING_FLOW_DIAGNOSIS_OFFRAMP,

        actions: [{ actionId: 'previousStep', command: { stepBackUsingHistory: true } }]
      },

      // DiagnosisOfframpWithoutSupport
      {
        stepId: BookingSteps.DiagnosisOfframpWithoutSupport,

        heading: screensData.noDiagnosisOfframp.heading,
        description: screensData.noDiagnosisOfframp.description,
        imageSrc: screensData.noDiagnosisOfframp.imageSrc,
        imageAlt: screensData.noDiagnosisOfframp.imageAlt,

        segmentEventName: segmentEventsNames.BOOKING_FLOW_DIAGNOSIS_OFFRAMP_WITHOUT_SUPPORT,

        actions: [{ actionId: 'previousStep', command: { stepBackUsingHistory: true } }]
      },

      // PostalCodeOfframp
      {
        stepId: BookingSteps.PostalCodeOfframp,
        heading: screensData.notQualified.location.heading,
        description: screensData.notQualified.location.description,
        imageSrc: screensData.notQualified.location.imageSrc,
        imageAlt: screensData.notQualified.location.imageAlt,

        segmentEventNames: {
          view: segmentEventsNames.BOOKING_FLOW_POSTAL_CODE_OFFRAMP,
          submit: segmentEventsNames.BOOKING_FLOW_POSTAL_CODE_OFFRAMP_BUTTON
        },

        actions: [
          { actionId: 'previousStep', command: { stepBackUsingHistory: true } },
          {
            actionId: 'homePage',
            command: { navigateTo: ROUTES.homePage }
          }
        ]
      },

      // AgeOfframp
      {
        stepId: BookingSteps.AgeOfframp,
        heading: screensData.notQualified.age.heading,
        description: screensData.notQualified.age.description,
        imageSrc: screensData.notQualified.age.imageSrc,
        imageAlt: screensData.notQualified.age.imageAlt,

        segmentEventNames: {
          view: segmentEventsNames.BOOKING_FLOW_AGE_OFFRAMP,
          submit: segmentEventsNames.BOOKING_FLOW_AGE_OFFRAMP_BUTTON
        },

        actions: [
          { actionId: 'previousStep', command: { stepBackUsingHistory: true } },
          {
            actionId: 'homePage',
            command: { navigateTo: ROUTES.homePage }
          }
        ]
      },

      // LegalGuardianOfframp
      {
        stepId: BookingSteps.LegalGuardianOfframp,

        heading: screensData.notQualified.heading,
        imageSrc: screensData.notQualified.imageSrc,
        imageAlt: screensData.notQualified.imageAlt,
        segmentEventName: segmentEventsNames.BOOKING_FLOW_LEGAL_GUARDIAN_OFFRAMP_VIEW,
        actions: []
      },

      // NotQualified
      {
        stepId: BookingSteps.NotQualified,

        heading: screensData.notQualified.heading,
        imageSrc: screensData.notQualified.imageSrc,
        imageAlt: screensData.notQualified.imageAlt,
        segmentEventNames: {
          view: segmentEventsNames.BOOKING_FLOW_NOT_QUALIFIED_VIEW,
          submit: segmentEventsNames.BOOKING_FLOW_NOT_QUALIFIED_CONTACT_SUBMIT
        },
        reasonListFormat: 'control',
        showEmail: true,

        actions: [
          {
            actionId: 'homePage',
            command: { navigateTo: ROUTES.homePage }
          }
          // no previous step intentionally
        ]
      }
    ]
  }
}
